<template>
  <BaseDialog
    v-model="isOpen"
    title="File Upload"
  >
    <div class="file-upload-modal">
      <v-alert v-if="uploadError" color="error" dark>
        {{ uploadError }}
      </v-alert>

      <v-alert border="left" colored-border text color="warning">
        <p class="mb-0">
          Attention: Please make sure the file structure matches the original Visa RDR file.
          Only .csv files are accepted. A file with the same name cannot be uploaded twice.
        </p>
        <p class="mb-0">Upload will start automatically after file selection.</p>
      </v-alert>

      <v-file-input
        v-model="selectedFile"
        accept=".csv"
        label="Select a file..."
        :loading="isUploading"
        :disabled="isUploading"
        show-size
        outlined
      ></v-file-input>

      <v-card-actions class="d-flex justify-center mt-3">
        <v-btn color="error" outlined @click="isOpen = false">Cancel</v-btn>
      </v-card-actions>
    </div>
  </BaseDialog>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';
import store from '@/store';
import BaseDialog from '@/views/shared/components/BaseDialog';

export default {
  name: 'ChargebackFileUploadModal',
  components: { BaseDialog },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    fileType: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const selectedFile = ref(null);
    const isUploading = ref(false);
    const uploadError = ref(null);

    let currentAttempt = 0;

    const isOpen = computed({
      get: () => props.value,
      set: newValue => emit('input', newValue),
    });

    function reset() {
      currentAttempt += 1;
      selectedFile.value = null;
      uploadError.value = null;
      isUploading.value = false;
    }

    function resolveErrorMessage(error) {
      return error?.response?.data?.message || 'Failed to upload the file.';
    }

    watch(() => props.value, isDialogOpen => {
      if (!isDialogOpen) {
        reset();
      }
    });

    watch(selectedFile, () => {
      if (!selectedFile.value) {
        return;
      }

      currentAttempt += 1;
      const attempt = currentAttempt;
      isUploading.value = true;
      uploadError.value = null;

      const formData = new FormData();
      formData.append('file', selectedFile.value);
      formData.append('fileType', props.fileType);

      store
        .dispatch('chargebackFileV2/uploadFile', formData)
        .then(() => {
          if (attempt !== currentAttempt) {
            return;
          }
          isOpen.value = false;
          emit('uploaded');
        })
        .catch(error => {
          if (attempt !== currentAttempt) {
            return;
          }
          uploadError.value = resolveErrorMessage(error);
          selectedFile.value = null;
        })
        .finally(() => {
          if (attempt === currentAttempt) {
            isUploading.value = false;
          }
        });
    });

    return {
      isOpen,
      selectedFile,
      isUploading,
      uploadError,
    };
  },
};
</script>
